import { User } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AnalysisResult, BodyAnalysisResult, BodyMeasurements } from './geminiService';

export interface StoredFaceAnalysis {
  result: AnalysisResult;
  capturedAt: any;
}

export interface StoredBodyAnalysis {
  result: BodyAnalysisResult;
  measurements: BodyMeasurements;
  measurementUnit: 'cm' | 'inches';
  capturedAt: any;
}

export interface UserProfile {
  uid: string;
  email?: string | null;
  phoneNumber?: string | null;
  displayName?: string | null;
  createdAt?: any;
  updatedAt?: any;
  gender?: string;
  stylePreference?: string;
  // Onboarding
  onboarded?: boolean;
  firstSeenAt?: any;
  // AI analysis snapshots (persisted across sessions and used to personalise reports)
  faceAnalysis?: StoredFaceAnalysis;
  bodyAnalysis?: StoredBodyAnalysis;
  // Raw inputs collected during onboarding BEFORE any AI analysis is run.
  // Analysis is run later via the paid AI-analysis flow, which can pre-fill
  // from these. Storing them does NOT trigger or imply analysis.
  pendingFacePhotoUrl?: string;
  pendingFacePhotoAt?: any;
  pendingMeasurements?: BodyMeasurements;
  pendingMeasurementUnit?: 'cm' | 'inches';
  pendingWeightUnit?: 'kg' | 'lb';
  pendingMeasurementsAt?: any;
  // True once the user has gone through the complete-profile step (saved or
  // explicitly skipped), so we don't keep redirecting them there on every login.
  profileStepDone?: boolean;
}

/**
 * A profile is considered "complete enough" to skip the onboarding
 * complete-profile step when we have face input (analysed OR a pending photo)
 * AND body input (analysed OR pending measurements). We also treat an explicit
 * skip (`profileStepDone`) as satisfying the gate so users aren't nagged.
 */
export const isProfileInputComplete = (p: UserProfile | null | undefined): boolean => {
  if (!p) return false;
  if (p.profileStepDone) return true;
  const hasFaceInput = !!p.faceAnalysis || !!p.pendingFacePhotoUrl;
  const hasBodyInput = !!p.bodyAnalysis || !!p.pendingMeasurements;
  return hasFaceInput && hasBodyInput;
};

/** Has the user actually completed AI analysis (used for the report-accuracy warning)? */
export const hasCompletedAnalysis = (p: UserProfile | null | undefined): { face: boolean; body: boolean } => ({
  face: !!p?.faceAnalysis,
  body: !!p?.bodyAnalysis,
});

/**
 * The body measurements that are MANDATORY before an AI styling report can be
 * generated. The remaining measurements (neck, biceps, inseam, …) stay optional.
 * Keep this in sync with the server-side list in functions/src/index.ts.
 */
export const REQUIRED_MEASUREMENT_KEYS: Array<keyof BodyMeasurements> = [
  'height', 'weight', 'bust', 'waist', 'hips', 'shoulders',
];

export const REQUIRED_MEASUREMENT_LABELS: Record<string, string> = {
  height: 'Height', weight: 'Weight', bust: 'Bust / Chest',
  waist: 'Waist', hips: 'Hips', shoulders: 'Shoulders',
};

/** Best-available measurements: analysed result first, else the pending ones. */
export const getEffectiveMeasurements = (
  p: UserProfile | null | undefined
): Partial<BodyMeasurements> | null =>
  p?.bodyAnalysis?.measurements || p?.pendingMeasurements || null;

export interface ReportInputStatus {
  ok: boolean;
  hasFace: boolean;
  hasRequiredBody: boolean;
  missingMeasurements: string[]; // human labels of missing required fields
}

/**
 * Whether the profile carries the inputs that are now MANDATORY to generate an
 * AI report: a face photo (analysed or pending) AND every required body
 * measurement. Used to gate report generation on the client; mirrored server-side.
 */
export const getReportInputStatus = (p: UserProfile | null | undefined): ReportInputStatus => {
  const hasFace = !!p?.faceAnalysis || !!p?.pendingFacePhotoUrl;
  const m = getEffectiveMeasurements(p);
  const missingMeasurements = REQUIRED_MEASUREMENT_KEYS.filter(
    (k) => !String((m?.[k] ?? '') as string).trim()
  ).map((k) => REQUIRED_MEASUREMENT_LABELS[k as string] || (k as string));
  const hasRequiredBody = missingMeasurements.length === 0;
  return { ok: hasFace && hasRequiredBody, hasFace, hasRequiredBody, missingMeasurements };
};

/**
 * Creates or updates the user document on auth. Returns whether the user is new
 * so the caller can route them through onboarding.
 */
export const upsertUserProfileFromAuth = async (user: User): Promise<{ isNew: boolean }> => {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  const isNew = !snap.exists();

  // CRITICAL: only write auth fields that actually have a value. Email/phone
  // users have a null `displayName` on the Firebase Auth object, so writing it
  // unconditionally on every refresh would wipe the name the user saved in
  // their profile. Never clobber a stored value with null.
  const payload: any = {
    uid: user.uid,
    updatedAt: serverTimestamp(),
  };
  if (user.email) payload.email = user.email;
  if (user.phoneNumber) payload.phoneNumber = user.phoneNumber;
  if (user.displayName) payload.displayName = user.displayName;

  if (isNew) {
    // On first creation it's fine to seed the (possibly null) auth values.
    if (payload.email === undefined) payload.email = user.email ?? null;
    if (payload.phoneNumber === undefined) payload.phoneNumber = user.phoneNumber ?? null;
    if (payload.displayName === undefined) payload.displayName = user.displayName ?? null;
    payload.createdAt = serverTimestamp();
    payload.firstSeenAt = serverTimestamp();
    payload.onboarded = false;
  }

  await setDoc(ref, payload, { merge: true });
  return { isNew };
};

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>): Promise<void> => {
  const ref = doc(db, 'users', uid);
  const cleaned = Object.fromEntries(Object.entries(updates).filter(([, v]) => v !== undefined));
  await setDoc(ref, { ...cleaned, updatedAt: serverTimestamp() }, { merge: true });
};

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const ref = doc(db, 'users', uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as UserProfile;
};

export const saveFaceAnalysisToProfile = async (
  uid: string,
  result: AnalysisResult
): Promise<void> => {
  await updateUserProfile(uid, {
    faceAnalysis: { result, capturedAt: Timestamp.now() },
  });
};

export const saveBodyAnalysisToProfile = async (
  uid: string,
  result: BodyAnalysisResult,
  measurements: BodyMeasurements,
  measurementUnit: 'cm' | 'inches' = 'cm'
): Promise<void> => {
  await updateUserProfile(uid, {
    bodyAnalysis: { result, measurements, measurementUnit, capturedAt: Timestamp.now() },
  });
};

export const markOnboarded = async (uid: string): Promise<void> => {
  await updateUserProfile(uid, { onboarded: true });
};

/** Store the onboarding face photo WITHOUT running analysis. */
export const savePendingFacePhoto = async (uid: string, photoUrl: string): Promise<void> => {
  await updateUserProfile(uid, {
    pendingFacePhotoUrl: photoUrl,
    pendingFacePhotoAt: Timestamp.now(),
  });
};

/** Store onboarding body measurements WITHOUT running analysis. */
export const savePendingMeasurements = async (
  uid: string,
  measurements: BodyMeasurements,
  measurementUnit: 'cm' | 'inches' = 'cm',
  weightUnit: 'kg' | 'lb' = 'kg'
): Promise<void> => {
  await updateUserProfile(uid, {
    pendingMeasurements: measurements,
    pendingMeasurementUnit: measurementUnit,
    pendingWeightUnit: weightUnit,
    pendingMeasurementsAt: Timestamp.now(),
  });
};

/** Mark the complete-profile step done (saved or skipped) so login stops routing there. */
export const markProfileStepDone = async (uid: string): Promise<void> => {
  await updateUserProfile(uid, { profileStepDone: true });
};

// ───────────────────────────────────────────────────────────────────────────
// Admin helpers (only succeed if the caller is an admin via Firestore rules)
// ───────────────────────────────────────────────────────────────────────────

export const getAllAppUsers = async (): Promise<UserProfile[]> => {
  const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ uid: d.id, ...(d.data() as Omit<UserProfile, 'uid'>) }));
};

export const updateUserAsAdmin = async (uid: string, patch: Partial<UserProfile>): Promise<void> => {
  // Same as updateUserProfile but exists as a separate name for clarity at call sites
  // and to surface admin intent in code reviews. Firestore rules enforce permission.
  await updateUserProfile(uid, patch);
};
